import { Flex } from "antd";
import React, { useEffect, useState } from "react";
import { zAttrLabelMeta } from "src/common/attributes/makeAttrLabel/types/ZAttrLabelMeta";
import { ZAttrLabelTreeData } from "src/common/attributes/makeAttrLabel/types/ZAttrLabelTreeData";
import { getViewInfo } from "src/common/attrView";
import { seedSeparators } from "src/common/attrView/viewFormItems/Separator";
import { findNodeByKey } from "src/common/findNodeByKey";
import { onError } from "src/common/onError";
import { AttrTypeName } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { classNames } from "src/common/classNames";
import { makeLabels4Leaves } from "../../../makeLabels4Leaves";
import { MakeLabelParams } from "../../../types/MakeLabelParams";
import { SimpleText } from "../../SimpleText/SimpleText";
import styles from "./UserListLabel.module.less";

export const getUserLabelStr = (
  userId: string,
  attrTreeData: ZAttrLabelTreeData,
  attributeList: ZAttribute[],
  typesMap: Record<string, string>,
  attribute?: ZAttribute,
) => {
  try {
    if (!attrTreeData || !attribute) {
      throw Error("system error");
    }
    const viewInfo = getViewInfo(attribute.viewType);
    const meta = zAttrLabelMeta.parse(attrTreeData.attrLabelMeta);
    if (
      meta.type !== AttrTypeName.usersList ||
      viewInfo?.component?.view !== "PersonCellInfo"
    )
      throw Error("system error");

    const roles = viewInfo.component.rolesViewAtts;
    const userRole = meta.roles[userId]?.[0];
    const viewAttrsIds = roles?.[userRole || ""] || [];
    const linkedNodes = meta.links[userId] || [];
    const linkedAttrsTree = linkedNodes
      .map((k) => findNodeByKey(k, [attrTreeData]))
      .filter(Boolean) as ZAttrLabelTreeData[];

    const sortedAttrsTree = Object.values(
      linkedAttrsTree.reduce(
        (acc, curr) => {
          const index = viewAttrsIds.findIndex((id) => id === curr?.attrId);
          if (index === -1) return acc;
          acc[index] = curr;
          return acc;
        },
        {} as Record<number, ZAttrLabelTreeData>,
      ),
    );
    const leavesLabels = makeLabels4Leaves(
      sortedAttrsTree,
      attributeList,
      typesMap,
    );

    return (
      <Flex wrap="wrap">
        {seedSeparators(
          leavesLabels.map((l, i) => ({ content: l, key: i })),
          // Поля одного пользователя всегда через пробел, а разделитель
          // из настроек отделяет пользователей друг от друга (LPLM-5858).
          "space",
        )}
      </Flex>
    );
  } catch (error) {
    onError(error);
    return "";
  }
};

interface PropsUserListLabel {
  params: MakeLabelParams;
}

export const UserListLabel: React.FC<PropsUserListLabel> = ({
  params: { attribute, labelTreeData, attributeList, typesMap },
}) => {
  const [label, setLabel] = useState<React.ReactNode>([]);
  const viewInfo = getViewInfo(attribute.viewType);

  useEffect(() => {
    try {
      const treeDataIdMap = new Map(labelTreeData?.map((d) => [d.attrId, d]));
      const attributesMap = new Map(attributeList?.map((a) => [a.id, a]));
      const attrTreeData = treeDataIdMap.get(attribute.id);
      if (!attrTreeData) return;
      const meta = zAttrLabelMeta.parse(attrTreeData.attrLabelMeta);
      if (meta.type !== AttrTypeName.usersList) {
        throw Error("system error");
      }
      const eIds = Object.keys(meta.links);
      const eLabels = eIds.map((id) => [
        id,
        getUserLabelStr(
          id,
          attrTreeData,
          attributeList,
          typesMap,
          attributesMap.get(attrTreeData.attrId),
        ),
      ]);
      const finalLabels = seedSeparators(
        eLabels.map(([keyNum, content]) => ({ key: String(keyNum), content })),
        viewInfo?.appearance?.view?.separator,
      );
      const multiple = eLabels.length > 1;
      setLabel(
        <SimpleText
          className={classNames([[multiple, styles.multiple], styles.box])}
          values={finalLabels}
          viewInfo={viewInfo}
        />,
      );
    } catch (error) {
      onError(error);
    }
  }, [attribute, labelTreeData, attributeList, typesMap, viewInfo]);

  return label;
};
